import { formatInvoiceDate } from "./nativeInvoice";
import { VariableDefinition, tokenizeVariableTemplate } from "./variable-field";

export type InvoiceEmailVariableKey =
    | "entityName"
    | "invoiceNumber"
    | "invoiceDate"
    | "dueDate";

export interface InvoiceEmailVariableContext {
    entityName: string | null;
    invoiceNumber: string | null;
    invoiceDate: string | null;
    dueDate: string | null;
}

export interface InvoiceEmailVariable extends VariableDefinition {
    key: InvoiceEmailVariableKey;
    resolve: (context: InvoiceEmailVariableContext) => string;
}

export function invoiceEmailVariableToken(
    key: InvoiceEmailVariableKey,
): string {
    return `{{${key}}}`;
}

function resolveOrKeepToken(
    value: string | null,
    key: InvoiceEmailVariableKey,
): string {
    const trimmed = value?.trim() ?? "";
    return trimmed ? trimmed : invoiceEmailVariableToken(key);
}

export const INVOICE_EMAIL_VARIABLES: InvoiceEmailVariable[] = [
    {
        key: "entityName",
        label: "Entity name",
        description: "The sending business's name",
        resolve: (context) =>
            resolveOrKeepToken(context.entityName, "entityName"),
    },
    {
        key: "invoiceNumber",
        label: "Invoice number",
        description: "The number assigned when the invoice is created",
        resolve: (context) =>
            resolveOrKeepToken(context.invoiceNumber, "invoiceNumber"),
    },
    {
        key: "invoiceDate",
        label: "Invoice date",
        description: "The invoice date, like Sep 17, 2026",
        resolve: (context) =>
            resolveOrKeepToken(
                formatInvoiceDate(context.invoiceDate) || null,
                "invoiceDate",
            ),
    },
    {
        key: "dueDate",
        label: "Due date",
        description: "The due date, like Sep 17, 2026",
        resolve: (context) =>
            resolveOrKeepToken(
                formatInvoiceDate(context.dueDate) || null,
                "dueDate",
            ),
    },
];

const INVOICE_EMAIL_VARIABLE_BY_KEY: Map<string, InvoiceEmailVariable> =
    new Map();
for (const variable of INVOICE_EMAIL_VARIABLES) {
    INVOICE_EMAIL_VARIABLE_BY_KEY.set(variable.key, variable);
}

const LEGACY_INVOICE_EMAIL_PLACEHOLDERS = [
    { from: "[invoice-number]", key: "invoiceNumber" },
    { from: "[#]", key: "invoiceNumber" },
    { from: "[entity]", key: "entityName" },
] as const satisfies ReadonlyArray<{
    from: string;
    key: InvoiceEmailVariableKey;
}>;

export function migrateLegacyInvoiceEmailTemplate(text: string): string {
    let next = text;
    for (const { from, key } of LEGACY_INVOICE_EMAIL_PLACEHOLDERS) {
        next = next.split(from).join(invoiceEmailVariableToken(key));
    }
    return next;
}

export function interpolateInvoiceEmailVariables(
    template: string,
    context: InvoiceEmailVariableContext,
): string {
    return tokenizeVariableTemplate(migrateLegacyInvoiceEmailTemplate(template))
        .map((token) =>
            token.kind === "variable"
                ? (INVOICE_EMAIL_VARIABLE_BY_KEY.get(token.key)?.resolve(
                      context,
                  ) ?? "")
                : token.value,
        )
        .join("");
}
